import { scalePow } from 'd3-scale'
import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils'
import styles from './VeSIS.pcss'
import { VESIS_DECIMALS } from '~/lib/SISDecimals'
import classNames from 'classnames'
import Spinner from '~/components/Spinner'
import { useGetVeSisBalance } from '~/lib/useGetVeSisBalance'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getFormattedValue } from '~/lib/getFormattedValue'

interface Props {
	threshold?: number
	balance?: BigNumber
}

const FORMATTER = Intl.NumberFormat('en', {
	//@ts-ignore
	notation: 'compact' as const,
})
const DEFAULT_THRESHOLD = 250

export function VeSIS({
	threshold = DEFAULT_THRESHOLD,
}: Props): JSX.Element {
	const balance = useGetVeSisBalance()

	const domain = useMemo(() => {
		return [0, threshold / 4, threshold, threshold * 10, threshold * 40]
	}, [])

	const scale = useMemo(() => {
		return scalePow()
			.domain(domain)
			.range([0, 0.25, 0.5, 0.75, 1])
			.clamp(true)
	}, [])

	const balanceString = balance ? formatUnits(balance, VESIS_DECIMALS) : '0'
	let number = Number.parseFloat(balanceString)
	if (Number.isNaN(number)) {
		number = 0
	}

	const formattedBalance = getFormattedValue(
		balance?.toString(),
		VESIS_DECIMALS,
		2
	)

	return (
		<div className={styles.container}>
			<div className={styles.title}>Total veSIS Balance:</div>
			<div className={styles.balanceRow}>
				{balance ? `${formattedBalance} veSIS` : <Spinner />}

				<Link to={'/vesis'} className={styles.veSISLink}>
					Get more veSIS
				</Link>
			</div>

			<hr className={styles.separator} />

			<div
				className={classNames(
					styles.progressBar,
					!balance && styles.loading
				)}
			>
				<div
					className={styles.progressBarProgress}
					style={{
						transform: `scaleX(${scale(number).toFixed(2)})`,
					}}
				/>
			</div>

			<div className={styles.scale}>
				{domain.map((value) => (
					<div
						style={{ '--value': scale(value).toFixed(2) } as any}
					>
						{FORMATTER.format(value)}
					</div>
				))}
			</div>
		</div>
	)
}
